/**
 * Sidebar aligned with LoanProX (https://loan-pro-x-dashboard-html-zeta.vercel.app/)
 * BNPL Plans and Merchants / Partners are excluded.
 */
import { DEFAULT_LAYOUT_SLUG, LAYOUT_SLUGS } from "../../constants/layout";
import { stripAppBasename } from "../../constants/appBase";
import { ACCOUNTING_PLACEHOLDERS, ACCOUNTING_SECTION } from "../../Components/Credora/Accounting/accountingPlaceholders";
import { FINANCE_REPORT_HUB_PATH } from "../../Components/Credora/Reports/financeReportConfig";

const base = process.env.PUBLIC_URL || "";

export { LAYOUT_SLUGS, DEFAULT_LAYOUT_SLUG };

const FILL_ICON_ALIASES = {
  calendar: "calender",
};

export function getMenuIconIds(iconKey) {
  const fillKey = FILL_ICON_ALIASES[iconKey] || iconKey;
  return {
    strokeId: `stroke-${iconKey}`,
    fillId: `fill-${fillKey}`,
  };
}

export function resolveLayoutSlug(pathname, fallbackLayout) {
  const parts = stripAppBasename(pathname).split("/").filter(Boolean);
  const last = parts[parts.length - 1];
  if (LAYOUT_SLUGS.includes(last)) {
    return last;
  }
  return fallbackLayout || DEFAULT_LAYOUT_SLUG;
}

export function menuHref(path, layoutSlug) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${normalized}/${layoutSlug}`;
}

function pathPartsWithoutLayout(pathname) {
  // window.pathname includes /credo360; menu paths do not (basename handles that)
  const parts = stripAppBasename(pathname).split("/").filter(Boolean);
  if (parts.length && LAYOUT_SLUGS.includes(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts;
}

function menuParts(menuPath) {
  return menuPath.replace(/\/$/, "").split("/").filter(Boolean);
}

/** Exact menu link match for /path/:layout (not sibling subpaths like /loans vs /loans/pending). */
export function menuPathMatches(pathname, menuPath) {
  if (!menuPath || !pathname) {
    return false;
  }
  const current = pathPartsWithoutLayout(pathname);
  const target = menuParts(menuPath);
  if (current.length !== target.length) {
    return false;
  }
  return target.every((segment, index) => current[index] === segment);
}

/** Section open when URL is under any child path (includes detail views). */
export function sectionMatchesPath(item, pathname) {
  if (item.path && menuPathMatches(pathname, item.path)) {
    return true;
  }
  if (!item.children) {
    return false;
  }
  const current = pathPartsWithoutLayout(pathname);
  return item.children.some((child) => {
    if (!child.path) {
      return false;
    }
    const prefix = menuParts(child.path);
    if (current.length < prefix.length) {
      return false;
    }
    const matchesPrefix = prefix.every((segment, index) => current[index] === segment);
    if (!matchesPrefix) {
      return false;
    }
    if (menuPathMatches(pathname, child.path)) {
      return true;
    }
    return current.length > prefix.length;
  });
}

export const MENUITEMS = [
  {
    menutitle: "",
    menucontent: "",
    Items: [
      {
        title: "Dashboard",
        icon: "home",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/dashboard/overview`, title: "Overview", type: "link" },
          { path: `${base}/credora/dashboard/key-metrics`, title: "Key Metrics", type: "link" },
          { path: `${base}/credora/dashboard/today-activity`, title: "Today's Activity", type: "link" },
          { path: `${base}/credora/dashboard/executive`, title: "Executive Overview", type: "link" },
          { path: `${base}/credora/dashboard/loan-officer`, title: "Loan Officer Dashboard", type: "link" },
          { path: `${base}/credora/dashboard/collections`, title: "Collections Dashboard", type: "link" },
          { path: `${base}/credora/dashboard/compliance`, title: "Compliance Dashboard", type: "link" },
          { path: `${base}/credora/dashboard/communications`, title: "Communications Dashboard", type: "link" },
          { path: `${base}/credora/dashboard/call-center`, title: "Call Center Dashboard", type: "link" },
        ],
      },
      {
        title: "AI Insights",
        icon: "widget",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/ai/credit-scoring`, title: "AI Credit Scoring", type: "link" },
          { path: `${base}/credora/ai/risk-alerts`, title: "Risk Alerts & Anomalies", type: "link" },
          { path: `${base}/credora/ai/churn-prediction`, title: "Churn & Default Prediction", type: "link" },
          { path: `${base}/credora/ai/recommendations`, title: "Smart Recommendations", type: "link" },
        ],
      },
      {
        title: "Loan Applications",
        icon: "file",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/loans`, title: "All Applications", type: "link" },
          { path: `${base}/credora/loans/new`, title: "New Application", type: "link" },
          { path: `${base}/credora/loans/groups`, title: "Group Applications", type: "link" },
          { path: `${base}/credora/loans/new/group`, title: "Group Loan Application", type: "link" },
          { path: `${base}/credora/loans/drafts`, title: "Draft Applications", type: "link" },
          { path: `${base}/credora/loans/pending`, title: "Pending Applications", type: "link" },
          { path: `${base}/credora/loans/approved`, title: "Approved", type: "link" },
          { path: `${base}/credora/loans/rejected`, title: "Rejected", type: "link" },
        ],
      },
      {
        title: "Active Loans",
        icon: "board",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/active-loan`, title: "All Active Loans", type: "link" },
          { path: `${base}/credora/schedule-emi`, title: "EMI Schedule", type: "link" },
          { path: `${base}/credora/overdue-loans`, title: "Overdue Loans", type: "link" },
          { path: `${base}/credora/restructured-loans`, title: "Restructured Loans", type: "link" },
        ],
      },
      {
        title: "Repayments",
        icon: "calendar",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/repayments/calendar`, title: "Repayment Calendar", type: "link" },
          { path: `${base}/credora/repayments/upcoming`, title: "Upcoming EMIs", type: "link" },
          { path: `${base}/credora/repayments/collected`, title: "Collected Today", type: "link" },
          { path: `${base}/credora/repayments/overdue`, title: "Overdue & Retries", type: "link" },
        ],
      },
      {
        title: "Risk & Collections",
        icon: "ecommerce",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/dashboard/collections`, title: "Risk Dashboard", type: "link" },
          { path: `${base}/credora/collections/alerts`, title: "Automated Risk Alerts", type: "link" },
          { path: `${base}/credora/collections`, title: "Collection Queue", type: "link" },
          { path: `${base}/credora/collections/ptp`, title: "Promise-to-Pay (PTP)", type: "link" },
          { path: `${base}/credora/collections/write-offs`, title: "Write-Offs", type: "link" },
        ],
      },
      {
        title: "Compliance & KYC",
        icon: "knowledgebase",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/compliance/kyc-verification`, title: "KYC Verification", type: "link" },
          { path: `${base}/credora/compliance/kyc/pending`, title: "Pending KYC", type: "link" },
          { path: `${base}/credora/compliance/kyc/verified`, title: "Verified KYC", type: "link" },
          { path: `${base}/credora/compliance/kyc/rejected`, title: "KYC Rejected", type: "link" },
          { path: `${base}/credora/compliance/control-registry`, title: "Control Registry", type: "link" },
          { path: `${base}/credora/compliance/audit-logs`, title: "Audit Logs", type: "link" },
          { path: `${base}/credora/compliance/operations`, title: "Go-Live & Security", type: "link" },
        ],
      },
      {
        title: "Borrowers",
        icon: "user",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/borrowers`, title: "All Borrowers", type: "link" },
          { path: `${base}/credora/borrowers/guarantors`, title: "Guarantors", type: "link" },
          { path: `${base}/credora/borrowers/collateral`, title: "Collateral Register", type: "link" },
          { path: `${base}/credora/borrowers/credit-history`, title: "Credit History", type: "link" },
        ],
      },
      {
        title: "Finance",
        icon: "project",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/finance/payouts`, title: "Payouts & Settlements", type: "link" },
          { path: `${base}/credora/finance/disbursements`, title: "Disbursement Logs", type: "link" },
          { path: `${base}/credora/finance/fees`, title: "Fees & Charges", type: "link" },
          { path: `${base}/credora/finance/working-capital`, title: "Working Capital", type: "link" },
          { path: `${base}/credora/finance/funding-accounts`, title: "Funding Accounts", type: "link" },
          { path: `${base}/credora/finance/product-allocations`, title: "Product Allocations", type: "link" },
          { path: `${base}/credora/finance/capital-ledger`, title: "Capital Ledger", type: "link" },
        ],
      },
      {
        title: ACCOUNTING_SECTION,
        icon: "ecommerce",
        type: "sub",
        active: false,
        children: ACCOUNTING_PLACEHOLDERS.filter((page) => page.inMenu).map((page) => ({
          path: `${base}/credora/${page.path}`,
          title: page.menuTitle,
          type: "link",
        })),
      },
      {
        title: "Reports",
        icon: "charts",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/reports/portfolio`, title: "Portfolio Summary", type: "link" },
          { path: `${base}/credora/reports/emi-breakdown`, title: "EMI Breakdown", type: "link" },
          { path: `${base}/credora/reports/aging`, title: "Aging Report", type: "link" },
          { path: `${base}/credora/reports/revenue`, title: "Revenue & Profitability", type: "link" },
          { path: `${base}/credora/${FINANCE_REPORT_HUB_PATH}`, title: "Financial Reports", type: "link" },
          { path: `${base}/credora/reports/finance/trial-balance`, title: "Trial Balance", type: "link" },
          { path: `${base}/credora/reports/finance/balance-sheet`, title: "Balance Sheet", type: "link" },
          { path: `${base}/credora/reports/finance/income-statement`, title: "Income Statement", type: "link" },
          { path: `${base}/credora/reports/finance/cash-flow`, title: "Cash Flow", type: "link" },
          { path: `${base}/credora/reports/finance/general-ledger`, title: "General Ledger", type: "link" },
          { path: `${base}/credora/reports/finance/budget-vs-actual`, title: "Budget vs Actual", type: "link" },
          { path: `${base}/credora/reports/finance/vat-summary`, title: "VAT Summary", type: "link" },
          { path: `${base}/credora/reports/working-capital`, title: "Working Capital", type: "link" },
          { path: `${base}/credora/reports/funding-accounts`, title: "Funding Accounts", type: "link" },
          { path: `${base}/credora/reports/digital-lending`, title: "BoT Digital Lending", type: "link" },
          { path: `${base}/credora/reports/finance/export`, title: "Export CSV / PDF", type: "link" },
        ],
      },
      {
        title: "Integrations & API",
        icon: "animation",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/integrations/sms-gateways`, title: "SMS Gateways", type: "link" },
          { path: `${base}/credora/integrations/smtp-gateways`, title: "SMTP Gateways", type: "link" },
          { path: `${base}/credora/integrations/whatsapp-webhooks`, title: "WhatsApp Webhooks", type: "link" },
          { path: `${base}/credora/integrations/scoring`, title: "Scoring APIs", type: "link" },
        ],
      },
      {
        title: "User Management",
        icon: "contact",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/users`, title: "All Users", type: "link" },
          { path: `${base}/credora/users/roles`, title: "Roles & Permissions", type: "link" },
          { path: `${base}/credora/users/teams`, title: "Teams", type: "link" },
        ],
      },
      {
        title: "Settings",
        icon: "form",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/settings`, title: "Overview", type: "link" },
          { path: `${base}/credora/settings/fee-configuration`, title: "Fee Configuration", type: "link" },
          { path: `${base}/credora/settings/loan-products`, title: "Loan Products", type: "link" },
          { path: `${base}/credora/settings/branches`, title: "Branches", type: "link" },
          { path: `${base}/credora/settings/roles`, title: "Roles", type: "link" },
          { path: `${base}/credora/settings/departments`, title: "Departments", type: "link" },
          { path: `${base}/credora/settings/communications`, title: "Communications", type: "link" },
          { path: `${base}/credora/settings/disbursement`, title: "Disbursement & Payout", type: "link" },
          { path: `${base}/credora/settings/treasury`, title: "Treasury & Working Capital", type: "link" },
          { path: `${base}/credora/settings/collections`, title: "Collection Rules", type: "link" },
          { path: `${base}/credora/settings/risk-alerts`, title: "Risk Alert Rules", type: "link" },
          { path: `${base}/credora/settings/kyc`, title: "KYC Setup", type: "link" },
          { path: `${base}/credora/settings/guarantors`, title: "Guarantor Setup", type: "link" },
          { path: `${base}/credora/settings/ai`, title: "AI & Ollama", type: "link" },
        ],
      },
      {
        title: "Communications",
        icon: "email",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/communication`, title: "Communication Center", type: "link" },
        ],
      },
      {
        title: "Support & Help",
        icon: "support-tickets",
        type: "sub",
        active: false,
        children: [
          { path: `${base}/credora/call-center`, title: "Agent Workspace", type: "link" },
          { path: `${base}/credora/support/help`, title: "Help Center", type: "link" },
          { path: `${base}/credora/support/docs`, title: "Operations manual", type: "link" },
          { path: `${base}/credora/support/contact`, title: "Contact Support", type: "link" },
        ],
      },
    ],
  },
];
